import {
  UpdateWorkspaceRequest,
  WorkspaceResult,
  WorkspaceService,
} from '@/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { useStore } from '@nanostores/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, PlusIcon, Save, UsersRound } from 'lucide-react';
import { toast } from 'sonner';
import { AddNewWorkspaceCollaborator } from '../../workspace/components/AddNewWorkspaceCollaborator';

export const Workspace = () => {
  const queryClient = useQueryClient();
  const user = useStore($user);
  const selectedOrgInSettings = useStore($orgInSettings);
  const selectedWorkspace = useStore($workspaceInSettings);
  const selectedWorkspaceInApp = useStore($workspace);
  const organizationSelected = useStore($orgnizationSelected);
  const workspaceSelected = useStore($workspaceSelected);

  const [editingWorkspaceName, setEditingWorkspaceName] = useState(false);
  const [workspaceName, setWorkspaceName] = useState(
    selectedWorkspace?.workspace?.name,
  );
  const [
    showAddWorkspaceCollaboratorDrawer,
    setShowAddWorkspaceCollaboratorDrawer,
  ] = useState(false);

  // query to get the collaborators of the workspace
  const collaborators = useQuery({
    queryKey: ['getWorkspaceCollaborators', selectedWorkspace?.workspace?.id],
    enabled: !!selectedWorkspace?.workspace?.id,
    queryFn: async () => {
      const res = await WorkspaceService.getCollaborators(
        selectedWorkspace?.workspace?.id || '',
      );
      return res;
    },
  });

  // mutation to update a workspace details
  const updateWorkspace = useMutation({
    mutationFn: async (payload: UpdateWorkspaceRequest) => {
      // call the api to create the organization
      const res = await WorkspaceService.updateWorkspace(
        selectedWorkspace?.workspace?.id || '',
        payload,
      );

      return res;
    },
    onSuccess: async data => {
      // invalidate queries here so that they are refetched
      await queryClient.invalidateQueries({
        queryKey: ['getWorkspaces', selectedOrgInSettings?.id],
      });

      const newWorkspaceData = {
        workspace: data?.workspace,
        projects: selectedWorkspace?.projects || [],
        invites: selectedWorkspace?.invites || 0,
        collaborators: selectedWorkspace?.collaborators || [],
      };

      $workspaceInSettings.set(newWorkspaceData as WorkspaceResult); // TODO: fix this type issue properly later

      if (
        selectedWorkspace?.workspace?.id ===
        selectedWorkspaceInApp?.workspace?.id
      ) {
        $workspace.set(newWorkspaceData as WorkspaceResult); // TODO: fix this type issue properly later
      }

      setEditingWorkspaceName(false);
    },
    onError: error => {
      console.error(error);
      toast('An error occurred', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  return (
    <div>
      <div className="flex w-full flex-col gap-3">
        <button
          key={selectedWorkspace?.workspace?.id}
          className={cn(
            'flex flex-col items-start gap-2 rounded-lg border p-4 text-left text-sm transition-all',
          )}
        >
          <div className="flex w-full items-center gap-4">
            <Avatar className="h-10 w-10 rounded-md border bg-muted">
              <AvatarImage src={selectedWorkspace?.workspace?.icon || ''} />
              <AvatarFallback>
                {selectedWorkspace?.workspace?.name
                  .slice(0, 2)
                  .toLocaleUpperCase()}
              </AvatarFallback>
            </Avatar>

            <div className="flex w-full flex-col gap-1">
              <div className="flex flex-1 items-center">
                <div className="flex flex-col items-start justify-center">
                  {editingWorkspaceName ? (
                    <Input
                      size={30}
                      className="h-6"
                      value={workspaceName}
                      onChange={e => {
                        setWorkspaceName(e.target.value);
                      }}
                      onKeyDown={e => {
                        if (e?.key === 'Enter') {
                          if (selectedWorkspace) {
                            updateWorkspace.mutateAsync({
                              name: workspaceName || '',
                            });
                            // setEditingOrgName(false);
                          }
                        }
                      }}
                      disabled={updateWorkspace?.isPending}
                    />
                  ) : (
                    <div className="text-sm font-semibold">
                      {selectedWorkspace?.workspace?.name}
                    </div>
                  )}

                  <div className="flex flex-1 text-muted-foreground">
                    <div className="flex flex-1">
                      <UsersRound size={14} className="mr-1 mt-1" />
                      {selectedWorkspace?.collaborators?.length} Collaborators
                    </div>
                  </div>
                </div>
                <div
                  className={cn(
                    'ml-auto items-center p-1 text-muted-foreground hover:rounded-md ',
                    'flex flex-col items-end justify-center',
                  )}
                >
                  {editingWorkspaceName ? (
                    <div className="flex">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingWorkspaceName(!editingWorkspaceName);
                          setWorkspaceName(selectedWorkspace?.workspace?.name);
                        }}
                        disabled={updateWorkspace?.isPending}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          updateWorkspace.mutateAsync({
                            name: workspaceName || '',
                          });
                        }}
                        disabled={updateWorkspace?.isPending}
                      >
                        {updateWorkspace?.isPending ? (
                          <Loader2 className="ml-0 h-3 animate-spin p-0" />
                        ) : (
                          <Save className="ml-0 h-3 p-0" />
                        )}
                        Save
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingWorkspaceName(!editingWorkspaceName);
                      }}
                      disabled={updateWorkspace?.isPending}
                    >
                      <Pencil className="ml-0 h-3 p-0" />
                      Edit
                    </Button>
                  )}
                  <div className="text-xs">
                    Created on{' '}
                    {new Date(
                      selectedWorkspace?.workspace?.createdAtUtc || '',
                    ).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </button>

        <div className="">
          <div className="mt-6">
            <p className="font-medium">Members</p>
            <p className="text-sm text-muted-foreground">
              Manage workspace members
            </p>
          </div>
        </div>

        <Tabs defaultValue="collaborators" className="w-full">
          <div className="flex flex-1 justify-between">
            <TabsList className="mb-4">
              <TabsTrigger value="collaborators">Collaborators</TabsTrigger>
              <TabsTrigger value="invitations">Invitations</TabsTrigger>
            </TabsList>
            <Button
              size="sm"
              onClick={() => {
                setShowAddWorkspaceCollaboratorDrawer(true);
              }}
            >
              <PlusIcon className="ml-0 h-3 p-0" />
              Add new collaborator
            </Button>
          </div>

          {collaborators?.isLoading ? (
            <div className="flex h-full w-full flex-1 items-center justify-center">
              <Loader2 />
            </div>
          ) : (
            <>
              <TabsContent
                value="collaborators"
                className="h-[350px] overflow-y-auto"
              >
                {collaborators?.data?.collaborators?.map(c => (
                  <div
                    key={c.collaborator?.id}
                    className="mb-4 flex items-center justify-between space-x-4 rounded-lg border p-2 pl-4 pr-4"
                  >
                    <div className="flex items-center space-x-4">
                      <Avatar className="">
                        <AvatarImage
                          src={c.collaborator?.photoUrl || ''}
                          alt="Profile menu"
                        />
                        <AvatarFallback>
                          {c.collaborator?.name
                            .slice(0, 2)
                            .toLocaleUpperCase() || 'A'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium leading-none">
                          {c.collaborator?.name || 'Name not found'}{' '}
                          {user?.uid === c.collaborator?.id && (
                            <Badge variant="outline">You</Badge>
                          )}
                          {/* TODO: Add role badge here */}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {c.collaborator?.email || 'Email not found'}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      {/* TODO: Add API to remove/edit the collaborator */}
                      {/* Remove Member button */}
                      <Button variant="destructive" size="sm">
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </TabsContent>
              <TabsContent
                value="invitations"
                className="h-[350px] overflow-y-auto"
              >
                {collaborators?.data?.invites?.map(c => (
                  <div
                    key={c?.id}
                    className="mb-4 flex items-center justify-between space-x-4 rounded-lg border p-2 pl-4 pr-4"
                  >
                    <div className="flex items-center space-x-4">
                      <Avatar className="">
                        <AvatarImage src={''} alt="Profile menu" />
                        <AvatarFallback>
                          {c?.name.slice(0, 2).toLocaleUpperCase() || 'A'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium leading-none">
                          {c?.name || 'Name not found'}{' '}
                          <Badge variant="outline">{c?.role}</Badge>
                          {/* TODO: Add role badge here */}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {c?.email || 'Email not found'}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      {/* TODO: Add API to remove/edit the collaborator */}
                      {/* Remove Member button */}
                      <Button variant="destructive" size="sm">
                        Revoke Invite
                      </Button>
                    </div>
                  </div>
                ))}
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
      <AddNewWorkspaceCollaborator
        open={showAddWorkspaceCollaboratorDrawer}
        setOpen={setShowAddWorkspaceCollaboratorDrawer}
        workspaceId={selectedWorkspace?.workspace?.id || ''}
      />
    </div>
  );
};
